import React from 'react';
import clsx from 'clsx';
import {
  useQuery,
  QueryClientProvider,
  QueryClient,
  useInfiniteQuery,
} from 'react-query';
import * as R from 'ramda';
import {useInView} from 'react-intersection-observer';
import {useLocalization, fetchSync} from '@shopify/hydrogen';
import groq from 'groq';
import sanityConfig from '../../../sanity.config';
import sanityClient from '@sanity/client';
import Blog from './Blog.client';
import type {SanityDocBlog} from '../../types';

const client = sanityClient(sanityConfig);

//components
import {LoadingSpinner} from '../collection/CollectionListingMain.client';

interface Props {
  totalBlogs: number;
}

function BlogListingFetch({totalBlogs}: Props) {
  const {ref, inView} = useInView();
  const {
    language: {isoCode: languageCode},
  } = useLocalization();

  const languageCodeForGRROQ = languageCode === 'ZH_TW' ? 'zh_TW' : 'en';
  const {
    data: blogs,
    error: fetchBlogsError,
    isFetching,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery(
    ['blogs'],
    async ({pageParam = {from: 8, to: 16}}) => {
      const res =
        pageParam.from &&
        fetchSync('/api/blogs', {
          method: 'post',
          body: JSON.stringify({
            from: pageParam.from,
            to: pageParam.to,
            slug: 'beginner',
            locale: languageCodeForGRROQ,
          }),
        }).json();

      const allBlogsFetched = pageParam.to >= totalBlogs;
      const nextFrom = allBlogsFetched ? undefined : pageParam.from + 9;
      const nextTo = allBlogsFetched ? undefined : pageParam.to + 9;
      return {
        data: res,
        nextPage: {from: nextFrom, to: nextTo},
      };
    },
    {
      getNextPageParam: (lastPage) => lastPage.nextPage,
    },
  );

  React.useEffect(() => {
    if (inView && hasNextPage) {
      fetchNextPage();
    }
  }, [inView]);

  return (
    <div className="md:py-10">
      <ul className="grid gap-y-8 gap-x-18 md:grid-cols-3 md:gap-y-24">
        {blogs &&
          blogs?.pages &&
          blogs?.pages.map((page) => {
            if (page && page.data) {
              return page.data.map((b: SanityDocBlog) => {
                return (
                  <li key={`${b._id}-${b._rev}`} className="md:col-span-1">
                    <Blog {...b} type="vertical" />
                  </li>
                );
              });
            }
            return null;
          })}
      </ul>
      <div
        className={clsx([
          totalBlogs > 7 ? 'py-10 md:mt-20' : 'hidden',
          isFetching && isFetchingNextPage ? 'opacity-100' : 'opacity-0',
        ])}
        ref={ref}
      >
        <div className="flex items-center justify-center">
          <div className="w-14">
            <LoadingSpinner className="text-hk-orange" />
          </div>
        </div>
      </div>
    </div>
  );
}

const queryClient = new QueryClient();

export default function BlogListingFetchWrapper({totalBlogs}: Props) {
  return (
    <QueryClientProvider client={queryClient}>
      <BlogListingFetch totalBlogs={totalBlogs} />
    </QueryClientProvider>
  );
}
